import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Edit, Save, X, StickyNote, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUpdateClient } from '@/hooks/clients/useUpdateClient';
import { Client } from '@/models/Client';
import { toast } from '@/hooks/use-toast';

interface ClientNotesEditorProps {
  client: Client;
}

const ClientNotesEditor: React.FC<ClientNotesEditorProps> = ({ client }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState(client.notes || '');
  const updateClientMutation = useUpdateClient();

  // Update local notes when client prop changes (e.g., after successful save)
  useEffect(() => {
    setNotes(client.notes || '');
  }, [client.notes]);

  const handleSave = async () => {
    try {
      const notesToSave = notes.trim();
      const updatedClient = await updateClientMutation.mutateAsync({
        id: client.id,
        clientData: {
          firstName: client.firstName,
          lastName: client.lastName,
          email: client.email,
          phone: client.phone,
          address: client.address,
          notes: notesToSave, // This will be empty string if cleared
          birthDate: client.birthDate,
          gender: client.gender,
          weightKg: client.weightKg,
          heightCm: client.heightCm,
        }
      });
      
      setIsEditing(false);
      toast({
        title: t('common.success'),
        description: t('clients.notesUpdated'),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('clients.notesUpdateError'),
        variant: 'destructive',
      });
    }
  };

  const handleClear = () => {
    setNotes('');
  };

  const handleCancel = () => {
    setNotes(client.notes || '');
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <div className="mt-3 md:mt-4 space-y-2">
        <div className="flex items-center gap-2 mb-2">
          <StickyNote className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">
            {t('clients.notes')}
          </span>
        </div>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('clients.notesPlaceholder')}
          className="min-h-[80px] resize-none"
          autoFocus
        />
        <div className="flex items-center gap-2 md:gap-3">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={updateClientMutation.isPending}
            className="h-7 md:h-8 px-2 md:px-3"
          >
            <Save className="h-3 w-3 md:mr-1" />
            <span className="text-xs md:text-sm">
              {updateClientMutation.isPending ? t('common.saving') : t('common.save')}
            </span>
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={handleClear}
            disabled={updateClientMutation.isPending}
            title={t('clients.clearNotes')}
            className="h-7 md:h-8 px-2 md:px-3"
          >
            <Trash2 className="h-3 w-3 md:mr-1" />
            <span className="hidden md:inline">{t('common.clear')}</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCancel}
            disabled={updateClientMutation.isPending}
            className="h-7 md:h-8 px-2 md:px-3"
          >
            <X className="h-3 w-3 md:mr-1" />
            <span className="hidden md:inline">{t('common.cancel')}</span>
          </Button>
        </div>
      </div>
    );
  }

  if (!client.notes) {
    return (
      <div className="mt-3 md:mt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleEdit}
          className="h-auto p-2 w-full justify-start text-left bg-muted/30 hover:bg-muted/50 border border-dashed border-muted-foreground/30"
        >
          <StickyNote className="h-4 w-4 mr-2 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {t('clients.addNotes')}
          </span>
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-3 md:mt-4 p-2 md:p-3 bg-muted/50 rounded-lg">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <StickyNote className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="text-xs font-medium text-muted-foreground">
              {t('clients.notes')}
            </span>
          </div>
          <p className="text-xs md:text-sm text-muted-foreground greek-text whitespace-pre-wrap">
            {client.notes}
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleEdit}
          className="h-5 w-5 md:h-6 md:w-6 p-0 flex-shrink-0"
        >
          <Edit className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

export default ClientNotesEditor;